import { type Cell, flexRender, type Table } from "@tanstack/react-table";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

type MobileTableCellProps<TData> = {
  cell: Cell<TData, unknown>;
  rowId: string;
  table: Table<TData>;
  cellClassName?: string;
};

export function MobileTableCell<TData>({
  cell,
  rowId,
  table,
  cellClassName,
}: MobileTableCellProps<TData>) {
  if (!cell.column.columnDef.header) return null;

  const header = table
    .getHeaderGroups()[0]
    ?.headers?.find((h) => h.column.id === cell.column.id);

  if (!header) return null;

  const headerContent = flexRender(
    cell.column.columnDef.header,
    header.getContext(),
  );

  if (!headerContent) return null;

  if (cell.column.columnDef.isMobileAction) {
    return (
      <div
        className={cnTwMerge(
          "flex items-center justify-between border-t border-border/primary gap-2 flex-wrap text-wrap p-4",
        )}
        data-testid={`mobile-action-row-${rowId}-${cell.column.id}`}
      >
        <div className="flex-1">{headerContent}</div>
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    );
  }

  if (cell.column.columnDef.isMobileActionGroup) {
    return (
      <div
        className={cnTwMerge(
          "flex items-center justify-end border-t border-border/primary gap-2 flex-wrap p-4",
        )}
        data-testid={`mobile-action-group-${rowId}-${cell.column.id}`}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 items-center border-t border-border/primary overflow-hidden"
      data-testid={`mobile-row-details-${rowId}-${cell.column.id}`}
    >
      <div
        className={cnTwMerge(
          "text-text/secondary w-30 shrink-0 flex items-center gap-1 p-4",
        )}
      >
        <div className="truncate overflow-hidden body-base-regular">
          {headerContent}
        </div>
      </div>
      <div
        className={cnTwMerge(
          "p-4 flex-1 body-base-regular truncate text-wrap",
          cell.column.columnDef.className,
          cellClassName,
        )}
      >
        <div className="w-fit">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      </div>
    </div>
  );
}
